import React, { useRef, useState } from 'react';

import Modal from '../Modal';
import apiClient, { API_URL } from '../../services/api';
import { AudioAsset, AudioMarker } from '../../types/api';
import { formatTime } from './formatTime';
import './AudioEditModal.css';

interface AudioEditModalProps {
  asset: AudioAsset;
  /** All known folder paths — feeds the "Move to" dropdown. */
  folders: string[];
  onClose: () => void;
  onUpdated: (asset: AudioAsset) => void;
  /** Delete the asset (confirmation handled by the caller). */
  onDelete: () => void;
  onError: (message: string | null) => void;
}

/**
 * Per-track editor opened from the library's file browser: rename,
 * preview, start-point markers (saved at the playhead, click to seek
 * back), move between folders. Every change PATCHes immediately —
 * there's no separate save step.
 */
export default function AudioEditModal({
  asset, folders, onClose, onUpdated, onDelete, onError,
}: AudioEditModalProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [nameDraft, setNameDraft] = useState(asset.name);
  const [markerLabel, setMarkerLabel] = useState('');
  const [saving, setSaving] = useState(false);

  async function patch(payload: Partial<Pick<AudioAsset, 'name' | 'markers' | 'folder' | 'is_favorite'>>): Promise<void> {
    setSaving(true);
    onError(null);
    try {
      const updated = await apiClient.patch<AudioAsset>(`/audiolib/audio/${asset.id}/`, payload);
      onUpdated(updated);
    } catch (err) {
      onError((err as Error).message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function commitName(): Promise<void> {
    const next = nameDraft.trim();
    if (!next || next === asset.name) {
      setNameDraft(asset.name);
      return;
    }
    await patch({ name: next });
  }

  async function addMarkerAtPlayhead(): Promise<void> {
    const time = audioRef.current?.currentTime ?? 0;
    const label = markerLabel.trim() || formatTime(time);
    setMarkerLabel('');
    const next: AudioMarker[] = [
      ...asset.markers,
      { id: `m_${Date.now().toString(36)}`, label, time: Math.round(time * 1000) / 1000 },
    ];
    await patch({ markers: next });
  }

  function seekTo(time: number): void {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = time;
    el.play().catch(() => { /* autoplay blocked — playhead still moved */ });
  }

  return (
    <Modal
      title={asset.name}
      open
      onClose={onClose}
      footer={(
        <>
          <a
            className="secondary btn--sm"
            href={`${API_URL}/audiolib/audio/${asset.id}/download/`}
            download
          >
            Download
          </a>
          <button type="button" className="danger btn--sm" onClick={onDelete} disabled={saving}>
            Delete
          </button>
          <button type="button" className="ghost btn--sm" onClick={onClose}>
            Close
          </button>
        </>
      )}
    >
      <div className="audio-edit">
        <label className="audio-edit-field">
          Name
          <input
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => { if (e.key === 'Enter') commitName(); }}
            maxLength={200}
          />
        </label>

        <label className="audio-edit-field">
          Folder
          <select
            value={asset.folder}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '__new__') {
                const dest = window.prompt('Move to folder (e.g. Albums/Synthwave)');
                if (dest && dest.trim()) patch({ folder: dest.trim() });
              } else {
                patch({ folder: v });
              }
            }}
            disabled={saving}
          >
            <option value="">Library (root)</option>
            {folders.map((f) => <option key={f} value={f}>{f}</option>)}
            {/* Current folder might be missing from `folders` right
              * after a move that emptied it — keep the select valid. */}
            {asset.folder && !folders.includes(asset.folder) && (
              <option value={asset.folder}>{asset.folder}</option>
            )}
            <option value="__new__">New folder…</option>
          </select>
        </label>

        {asset.file_url && (
          <audio ref={audioRef} src={asset.file_url} controls preload="metadata" className="audio-edit-player" />
        )}

        <div className="audio-edit-markers">
          <div className="section-label">Start markers</div>
          {asset.markers.length === 0 && (
            <span className="muted">None yet — play to a good spot, then save it below.</span>
          )}
          <ul className="audio-edit-marker-list">
            {asset.markers.map((m) => (
              <li key={m.id} className="audio-edit-marker">
                <button
                  type="button"
                  className="ghost btn--sm"
                  onClick={() => seekTo(m.time)}
                  title={`Jump to ${formatTime(m.time)}`}
                >
                  {m.label} · {formatTime(m.time)}
                </button>
                <button
                  type="button"
                  className="ghost btn--icon-sm"
                  onClick={() => patch({ markers: asset.markers.filter((x) => x.id !== m.id) })}
                  aria-label={`Delete marker ${m.label}`}
                  disabled={saving}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <div className="audio-edit-marker-add">
            <input
              type="text"
              placeholder="Marker label (e.g. Chorus)"
              value={markerLabel}
              onChange={(e) => setMarkerLabel(e.target.value)}
              maxLength={100}
            />
            <button
              type="button"
              className="secondary btn--sm"
              onClick={addMarkerAtPlayhead}
              disabled={saving || !asset.file_url}
            >
              + Save current position
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
